/**
 * =============================================================================
 * REPLY STATUS BADGE COMPONENT
 * =============================================================================
 *
 * Color-coded badge for displaying reply moderation status.
 * Uses BigDesign Badge component for consistency with StatusBadge pattern.
 *
 * GAP-008: Admin Reply & Solution Management UI
 *
 * =============================================================================
 */

import React from 'react';
import { StatusChip } from '../common/StatusChip';
import type { ReplyModerationStatus } from '@find-my-part/types';

/**
 * Props for ReplyStatusBadge component.
 */
export interface ReplyStatusBadgeProps {
  /** The reply moderation status */
  status: ReplyModerationStatus;
  /** Optional test ID */
  'data-testid'?: string;
}

/**
 * Status configuration for badge display.
 */
interface StatusConfig {
  label: string;
  variant: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
}

/**
 * Maps reply moderation status to badge configuration.
 *
 * - PENDING_REVIEW: warning (yellow) — needs attention
 * - APPROVED: success (green) — visible to public
 * - REJECTED: danger (red) — blocked
 * - HIDDEN: secondary (gray) — removed from view
 */
const REPLY_STATUS_CONFIG: Record<ReplyModerationStatus, StatusConfig> = {
  PENDING_REVIEW: { label: 'Pending', variant: 'warning' },
  APPROVED: { label: 'Approved', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
  HIDDEN: { label: 'Hidden', variant: 'secondary' },
};

/**
 * Badge component for reply moderation status.
 *
 * @example
 * ```tsx
 * <ReplyStatusBadge status="APPROVED" />
 * <ReplyStatusBadge status="PENDING_REVIEW" />
 * ```
 */
export function ReplyStatusBadge({
  status,
  'data-testid': testId,
}: ReplyStatusBadgeProps): React.ReactElement {
  const config = REPLY_STATUS_CONFIG[status];

  return (
    <StatusChip
      variant={config.variant}
      label={config.label}
      testId={testId}
      ariaLabel={`Reply status: ${config.label}`}
    />
  );
}
